import gql from 'graphql-tag';

import { Query, Mutation } from 'core';

import { USER_ERROR_FIELDS } from 'core/errors';

import { GROUP_FIELDS } from 'apps/group/gql';

export const SHARED_FILE_REVISION = gql`
  fragment sharedFileRevision on SharedFileRevision {
    id
    version
    dateCreated
    lastUpdated
    title
    creator {
      id
      username
    }
    file {
      id
      filename
      mimeType
      path
      size
      completed
      fileLastModified
      permalink
      canDownload
      url
      private
      originalFile {
        id
      }
      space {
        id
        parent {
          id
        }
      }
      conversionGltfjson {
        id
        status
        reason
        errorCode
        viewerSemver
        convertedFile {
          id
        }
      }
    }
  }
`;

const SHARED_FILE_SINGLE = gql`
  ${SHARED_FILE_REVISION}
  ${GROUP_FIELDS}
  fragment sharedFile on SharedFile {
    id
    canUpdate
    canDelete
    isPrivate
    creator {
      id
      username
    }
    slug
    name
    revision {
      ...sharedFileRevision
    }
    context {
      id
      parentObj {
        id
        canUpdate
      }
    }
    groups {
      ...groupFields
    }
    space {
      id
      spaceType
      trialPeriodEnd
      hasPrivateProjectsSubscription
      remainingSharedFiles
      content {
        id
        slug
      }
    }
    inSpace {
      id
      whichTypes
      spaceType
      parent {
        id
        spaceType
        content {
          id
          slug
        }
      }
      content {
        id
        slug
      }
    }
  }
`;

const CREATE_SHARED_FILE_MUTATION = gql`
  ${SHARED_FILE_SINGLE}
  ${USER_ERROR_FIELDS}
  mutation CreateSharedFile($createSharedFileInput: CreateSharedFileInput!) {
    createSharedFile(createSharedFileInput: $createSharedFileInput) {
      sharedFile {
        ...sharedFile
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class CreateSharedFileMutation extends Mutation {
  static conf = {
    GQL: CREATE_SHARED_FILE_MUTATION,
    inputName: 'createSharedFileInput',
    resultPath: 'createSharedFile.sharedFile',
    errorPath: 'createSharedFile.userErrors',
  };
}

const UPDATE_SHARED_FILE_MUTATION = gql`
  ${SHARED_FILE_SINGLE}
  ${USER_ERROR_FIELDS}
  mutation UpdateSharedFile($updateSharedFileInput: UpdateSharedFileInput!) {
    updateSharedFile(updateSharedFileInput: $updateSharedFileInput) {
      sharedFile {
        ...sharedFile
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class UpdateSharedFileMutation extends Mutation {
  static conf = {
    GQL: UPDATE_SHARED_FILE_MUTATION,
    inputName: 'updateSharedFileInput',
    resultPath: 'updateSharedFile.sharedFile',
    errorPath: 'updateSharedFile.userErrors',
  };
}

const SHARED_FILE_QUERY = gql`
  ${SHARED_FILE_SINGLE}
  ${USER_ERROR_FIELDS}
  query SharedFile($id: ID!, $version: String) {
    sharedFile(id: $id, version: $version) {
      result(id: $id, version: $version) {
        ...sharedFile
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class SharedFileQuery extends Query {
  static conf = {
    GQL: SHARED_FILE_QUERY,
    resultPath: 'sharedFile.result',
    errorPath: 'sharedFile.userErrors',
    varNames: ['id', 'version'],
  };
}

const SHARED_FILE_REVISIONS_QUERY = gql`
  ${SHARED_FILE_REVISION}
  ${USER_ERROR_FIELDS}
  query SharedFileRevisions($id: ID!, $batchSize: Int, $cursor: String) {
    sharedFile(id: $id) {
      result(id: $id) {
        revisions(first: $batchSize, after: $cursor) {
          pageInfo {
            startCursor
            endCursor
            hasNextPage
          }
          totalCount
          edges {
            node {
              ...sharedFileRevision
            }
          }
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class SharedFileRevisionsQuery extends Query {
  static conf = {
    GQL: SHARED_FILE_REVISIONS_QUERY,
    resultPath: 'sharedFile.result.revisions',
    errorPath: 'sharedFile.userErrors',
    varNames: ['id', 'batchSize', 'cursor'],
  };
}

const SHARED_FILE_CONNECTION = gql`
  fragment sharedFileConnection on SharedFile {
    id
    name
    revisions {
      totalCount
    }
    revision {
      id
      lastUpdated
      file {
        id
        filename
        conversionGltfjson {
          id
          status
        }
      }
    }
    space {
      id
      spaceType
      content {
        id
        title
        slug
      }
    }
  }
`;

const PROFILE_SHARED_FILES_QUERY = gql`
  ${SHARED_FILE_CONNECTION}
  ${USER_ERROR_FIELDS}
  query ProfileSharedFiles(
    $username: String!
    $batchSize: Int
    $cursor: String
  ) {
    profile(username: $username) {
      result(username: $username) {
        id
        sharedFiles(sortBy: "recent", first: $batchSize, after: $cursor) {
          pageInfo {
            startCursor
            endCursor
            hasNextPage
          }
          totalCount
          edges {
            node {
              ...sharedFileConnection
            }
          }
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class ProfileSharedFilesQuery extends Query {
  static conf = {
    GQL: PROFILE_SHARED_FILES_QUERY,
    resultPath: 'profile.result.sharedFiles',
    errorPath: 'profile.userErrors',
    varNames: ['username', 'batchSize', 'cursor'],
  };
}

const INITIATIVE_SHARED_FILES_QUERY = gql`
  ${SHARED_FILE_CONNECTION}
  ${USER_ERROR_FIELDS}
  query InitiativeSharedFiles(
    $slug: String!
    $batchSize: Int
    $cursor: String
  ) {
    initiative(slug: $slug) {
      result {
        id
        sharedFiles(sortBy: "recent", first: $batchSize, after: $cursor) {
          pageInfo {
            startCursor
            endCursor
            hasNextPage
          }
          totalCount
          edges {
            node {
              ...sharedFileConnection
            }
          }
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class InitiativeSharedFilesQuery extends Query {
  static conf = {
    GQL: INITIATIVE_SHARED_FILES_QUERY,
    resultPath: 'initiative.result.sharedFiles',
    errorPath: 'initiative.userErrors',
    varNames: ['slug', 'batchSize', 'cursor'],
  };
}

const SHARED_FILE_COLLABORATORS_QUERY = gql`
  ${GROUP_FIELDS}
  ${USER_ERROR_FIELDS}
  query SharedFileCollaborators($id: ID!) {
    sharedFile(id: $id) {
      result(id: $id) {
        id
        groups {
          ...groupFields
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class SharedFileCollaboratorsQuery extends Query {
  static conf = {
    GQL: SHARED_FILE_COLLABORATORS_QUERY,
    resultPath: 'sharedFile.result',
    errorPath: 'sharedFile.userErrors',
    varNames: ['id'],
  };
}

const DELETE_SHARED_FILE_MUTATION = gql`
  ${USER_ERROR_FIELDS}
  mutation DeleteSharedFile($deleteSharedFileInput: DeleteSharedFileInput!) {
    deleteSharedFile(deleteSharedFileInput: $deleteSharedFileInput) {
      result
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class DeleteSharedFileMutation extends Mutation {
  static conf = {
    GQL: DELETE_SHARED_FILE_MUTATION,
    inputName: 'deleteSharedFileInput',
    resultPath: 'deleteSharedFile.result',
    errorPath: 'deleteSharedFile.userErrors',
  };
}
